import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Award, Carrot, Ruler, ShieldCheck, Heart, Star } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { FadeUp } from "../components/site/motion";
import { DemoNote } from "../components/site/Badges";
import { Container, SectionHead } from "../components/sections/SectionHead";
import { M } from "../data/site";

const LEVELS = [
  { id: "sprouts", name: "Sprouts", age: "Ages 4–6", lesson: "Rainbow Fruit Skewers", skills: ["Washing produce", "Counting colours", "Threading safely"], text: "Little hands sort fruit by colour and build their own rainbow — learning that eating the rainbow helps bodies grow strong." },
  { id: "sous", name: "Sous Chefs", age: "Ages 7–9", lesson: "Build-Your-Own Veggie Wraps", skills: ["Measuring", "Spreading & rolling", "Table-knife cutting"], text: "Kids plan a wrap with a protein, two vegetables and a sauce, measuring ingredients and learning why each part matters." },
  { id: "junior", name: "Junior Chefs", age: "Ages 10–12", lesson: "One-Pan Garlic Chicken & Veg", skills: ["Stove safety with an adult", "Timing", "Seasoning to taste"], text: "With an adult nearby, juniors cook a full family dinner and learn how heat, timing and seasoning work together." },
];
const BADGES = [
  { icon: Carrot, t: "Rainbow Plate" }, { icon: Ruler, t: "Measuring Master" }, { icon: ShieldCheck, t: "Safe Helper" }, { icon: Heart, t: "Kind Cook" }, { icon: Star, t: "Taste Explorer" }, { icon: Award, t: "Family Chef" },
];

export default function LittleChefs() {
  const [lvl, setLvl] = useState(LEVELS[0]);
  const [earned, setEarned] = useState([]);
  return (
    <div data-testid="little-chefs-page">
      <PageHero eyebrow="Little Chefs Academy" img={M("littlechefs.jpg")} alt="Honesti cooking with three young kids in chef hats"
        lines={[{ text: "Tiny chefs." }, { text: "Big confidence.", className: "text-grad" }]}
        intro="Fun, age-appropriate cooking and nutrition lessons that help kids build confidence, creativity and lifelong healthy habits — with grown-ups always in the loop.">
        <Link to="/waitlist" data-testid="chefs-waitlist-btn" className="btn-primary mt-8">Get early access <ArrowRight className="h-4 w-4" /></Link>
      </PageHero>
      <Container testId="chefs-levels">
        <SectionHead eyebrow="Sample lessons by age" title={<>Pick a <span className="text-grad">level</span></>} sub="Illustrative lessons — final curriculum may differ." />
        <div className="mb-8 flex flex-wrap gap-2">
          {LEVELS.map((l) => (
            <button key={l.id} onClick={() => setLvl(l)} data-testid={`level-tab-${l.id}`} className={`relative rounded-full px-6 py-3 text-sm font-semibold transition-colors ${lvl.id === l.id ? "text-ink" : "text-slate-200 hover:text-white"}`}>
              {lvl.id === l.id && <motion.span layoutId="lvl-pill" className="absolute inset-0 rounded-full bg-cyan-300" />}
              <span className="relative">{l.name} · {l.age}</span>
            </button>
          ))}
        </div>
        <div className="glass relative min-h-[300px] overflow-hidden rounded-[2rem] p-6 lg:p-12">
          <AnimatePresence initial={false}>
            <motion.div key={lvl.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, position: "absolute" }} transition={{ duration: 0.5 }} className="grid gap-8 lg:grid-cols-2" data-testid="level-lesson">
              <div>
                <p className="eyebrow">{lvl.age} · Sample lesson</p>
                <h3 className="mt-3 font-display text-4xl font-bold text-white">{lvl.lesson}</h3>
                <p className="mt-4 text-slate-200">{lvl.text}</p>
              </div>
              <div>
                <p className="eyebrow mb-4">Skills practiced</p>
                <ul className="space-y-2">{lvl.skills.map((s) => <li key={s} className="rounded-xl bg-white/5 px-4 py-3 text-white">{s}</li>)}</ul>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Container>
      <section className="mx-auto max-w-[1440px] px-5 pb-24 lg:px-12">
        <SectionHead eyebrow="Badges" title={<>Collect <span className="text-grad">chef badges</span></>} sub="Tap a badge to see how earning feels." right={<div className="max-w-sm"><DemoNote>Demonstration only. Badges are not saved.</DemoNote></div>} />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {BADGES.map((b, i) => {
            const on = earned.includes(b.t);
            return (
              <FadeUp key={b.t} delay={i * 0.06}>
                <motion.button whileTap={{ scale: 0.92 }} onClick={() => setEarned((e) => (on ? e.filter((x) => x !== b.t) : [...e, b.t]))} data-testid={`badge-${i}`}
                  className={`flex w-full flex-col items-center gap-3 rounded-2xl border p-6 transition-[border-color,background-color,box-shadow] duration-500 ${on ? "border-cyan-300 bg-cyan-400/15 shadow-[0_0_30px_rgba(34,211,238,0.35)]" : "border-cyan-400/20 bg-deep/60"}`}>
                  <span className={`grid h-16 w-16 place-items-center rounded-full ${on ? "bg-cyan-300 text-ink" : "border border-cyan-400/40 text-cyan-200"}`}><b.icon className="h-7 w-7" /></span>
                  <span className="text-sm font-semibold text-white">{b.t}</span>
                </motion.button>
              </FadeUp>
            );
          })}
        </div>
      </section>
    </div>
  );
}
